/**
 * W&B Free Shipping Bar Override
 * Patches the free shipping threshold logic based on URL/market
 * Safe to use in production - doesn't modify theme.min.js
 */
(function() {
  'use strict';

  // =============================================
  // CONFIGURATION - Your custom thresholds (in dollars/pounds/euros)
  // =============================================
  const THRESHOLDS = {
    '/en-us': 150,
    '/en-ca': 150,
    '/en-nz': 150,
    '/en-gb': 100,
    '/en-de': 100,
    '/en-fr': 100,
    '/en-nl': 100,
    '/en-es': 100
  };

  // =============================================
  // SELECTORS (these are stable, not minified)
  // =============================================
  const SELECTORS = {
    freeShippingBar: '.free-shipping-bar',
    progressBar: '.free-shipping-bar__bar',
    message: '.free-shipping-bar__message'
  };

  const CLASSES = {
    loaded: 'free-shipping-bar--loaded',
    success: 'free-shipping-bar--success'
  };

  // =============================================
  // HELPER: Format money using theme settings
  // =============================================
  function formatMoney(cents) {
    const format = window.theme?.moneyFormat || '${{amount}}';
    
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? (decimal + parts[1]) : '';
      return dollarsAmount + centsAmount;
    }

    switch (format.match(placeholderRegex)?.[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }

  // =============================================
  // CORE: Get threshold based on URL
  // =============================================
  function getThresholdForCurrentMarket(fallbackThreshold) {
    const path = window.location.pathname.toLowerCase();
    
    for (const [urlSegment, threshold] of Object.entries(THRESHOLDS)) {
      if (path.includes(urlSegment)) {
        return threshold * 100; // Convert to cents
      }
    }
    
    // Fallback: Use the theme setting (already in base currency)
    return (parseInt(fallbackThreshold, 10) || 0) * 100;
  }

  // =============================================
  // CORE: Update free shipping bar
  // =============================================
  function updateFreeShippingBar(node) {
    const { threshold, cartTotal, freeShippingSuccessMessage, freeShippingPendingMessage } = node.dataset;
    
    const cartTotalCents = parseInt(cartTotal, 10) || 0;
    const thresholdInCents = getThresholdForCurrentMarket(threshold);

    // Update message
    const messageEl = node.querySelector(SELECTORS.message);
    if (messageEl) {
      if (cartTotalCents >= thresholdInCents) {
        node.classList.add(CLASSES.success);
        messageEl.innerText = freeShippingSuccessMessage;
      } else {
        node.classList.remove(CLASSES.success);
        const remainder = Math.abs(cartTotalCents - thresholdInCents);
        messageEl.innerHTML = freeShippingPendingMessage.replace(
          '{{ remaining_amount }}',
          `<span class="fs-body-bold">${formatMoney(remainder)}</span>`
        );
      }
    }

    // Update progress bar
    const progressBar = node.querySelector(SELECTORS.progressBar);
    if (progressBar) {
      const progress = cartTotalCents < thresholdInCents 
        ? (cartTotalCents / thresholdInCents) * 100 
        : 100;
      progressBar.style.setProperty('--progress-width', `${progress}%`);
    }

    // Mark as loaded
    node.classList.add(CLASSES.loaded);
  }

  // =============================================
  // INITIALIZATION: Run after theme.min.js
  // =============================================
  function init() {
    // Find all free shipping bars and override them
    const bars = document.querySelectorAll(SELECTORS.freeShippingBar);
    bars.forEach(updateFreeShippingBar);

    // Watch for dynamically added bars (e.g., quick cart, AJAX updates)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a free shipping bar
            if (node.matches?.(SELECTORS.freeShippingBar)) {
              // Small delay to let the original function run first, then override
              setTimeout(() => updateFreeShippingBar(node), 10);
            }
            // Check if it contains a free shipping bar
            const innerBars = node.querySelectorAll?.(SELECTORS.freeShippingBar);
            if (innerBars?.length) {
              setTimeout(() => innerBars.forEach(updateFreeShippingBar), 10);
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also listen for cart updates
    document.addEventListener('cart:updated', () => {
      setTimeout(() => {
        document.querySelectorAll(SELECTORS.freeShippingBar).forEach(updateFreeShippingBar);
      }, 50);
    });
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();